import { ReactNode, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { PopUpType } from '../../../common/components/PopUp';
import { useProtocol } from '../context';
import { Protocol } from '../../../api/protocolsApi';
import { useApi } from '../../../api/useApi';
import { useReferences } from '../../../helpers/context_manager/useReferences';
import { toOptions } from '../../../helpers/toOptions';
import { formatRuianAddress } from '../../../api/ruianApi';
import {
  AsyncOption,
  validateDateAfterOrEqual,
  validateMaxDate,
  notEqual
} from '../../../common/components/Form';
import { Diagnosis } from '../../../api/referencesApi';

type FormValues = Protocol & {
  type_of_examination: number;
  clinical_dg4: number;
  clinical_dg4_code: string;
};

export const useAutopsyProtocol = () => {
  const api = useApi();
  const { protocol, loading, setProtocol } = useProtocol();
  const { references, loading: referencesLoading } = useReferences();
  const [message, setMessage] = useState<{ type: PopUpType; message: ReactNode } | undefined>(
    undefined
  );
  const [printing, setPrinting] = useState(false);

  const methods = useForm<FormValues>({
    defaultValues: {
      id: 0,
      protocol_year: 0,
      protocol_number: 0,
      deceased: 0,
      deceased_display: '',
      is_not_autopsied: false,
      forensic_consultation: false,
      autopsy_date: '',
      autopsy_time: '',
      applicant: 0,
      applicant_name: '',
      blocks: '',
      slides: '',
      date_of_death: '',
      time_of_death: '',
      death_time_unknown: false,
      death_date_unknown: false,
      place_of_death: '',
      cause_of_death_autopsy: '',
      examining_physician: 0,
      examining_physician_name: '',
      examining_physician_text: '',
      pathologist1: 0,
      pathologist1_name: '',
      pathologist2: 0,
      pathologist2_name: '',
      created_by: 0,
      created_by_name: '',
      lab_technician: 0,
      lab_technician_name: '',
      attendees: null,
      classification: 0,
      classification_name: '',
      autopsy_diagnosis: '',
      clinical_dg1: 0,
      clinical_dg2: 0,
      clinical_dg3: 0,
      clinical_dg4: 0,
      autopsy_dg1: 0,
      autopsy_dg2: 0,
      autopsy_dg3: 0,
      mechanism_dg1: 0,
      mechanism_dg2: 0,
      mechanism_dg3: 0,
      mechanism_dg4: 0,
      mechanism_dg5: 0,
      clinical_dg1_code: '',
      clinical_dg2_code: '',
      clinical_dg3_code: '',
      clinical_dg4_code: '',
      autopsy_dg1_code: '',
      autopsy_dg2_code: '',
      autopsy_dg3_code: '',
      mechanism_dg1_code: '',
      mechanism_dg2_code: '',
      mechanism_dg3_code: '',
      mechanism_dg4_code: '',
      mechanism_dg5_code: '',
      note: '',
      is_deactivated: false,
      is_locked: false,
      last_printed_at: '',
      sent_to_uzis_at: '',
      uzis_identifier: '',
      is_sent_to_uzis: false,
      type_of_examination: 0,
      confidentiality_waiver: false,
      sent_side_effects_report: false,
      sent_to_NOR: false,
      sent_to_TBC: false,
      is_interrupted: false,
      is_death_time_estimate: false,
      place_of_death_ruian: null,
      is_stillborn: false,
      drug_related_death: false,
      precious_metal_implants: false,
      precious_metal_implants_note: '',
    },
    mode: "onChange"
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!protocol?.id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }
    try {
      // Příprava dat pro API - odstranění polí, která nejsou v ProtocolFormData
      const {
        id,
        deceased_display,
        applicant_name,
        examining_physician_name,
        pathologist1_name,
        pathologist2_name,
        created_by_name,
        lab_technician_name,
        classification_name,
        clinical_dg1_code,
        clinical_dg2_code,
        clinical_dg3_code,
        clinical_dg4_code,
        autopsy_dg1_code,
        autopsy_dg2_code,
        autopsy_dg3_code,
        mechanism_dg1_code,
        mechanism_dg2_code,
        mechanism_dg3_code,
        mechanism_dg4_code,
        mechanism_dg5_code,
        last_printed_at,
        sent_to_uzis_at,
        uzis_identifier,
        is_sent_to_uzis,
        type_of_examination,
        clinical_dg4,
        ...protocolData
      } = data;

      // Namapovať type_of_examination → examination_type pre PATCH payload
      const dataToSanitize = { ...protocolData, examination_type: type_of_examination };

      // Sanitizace dat - převod prázdných polí a neplatných hodnot na null nebo 0
      const sanitizedData = Object.entries(dataToSanitize).reduce((acc, [key, value]) => {
        // Pro číselná pole (diagnózy, osoby) - převést prázdné pole nebo neplatnou hodnotu na null
        if (
          key.includes('_dg') ||
          [
            'payment_type',
            'applicant',
            'examining_physician',
            'pathologist1',
            'pathologist2',
            'created_by',
            'lab_technician',
            'classification',
            'deceased',
            'place_of_death_ruian',
            'examination_type'
          ].includes(key)
        ) {
          if (Array.isArray(value) && value.length === 0) {
            acc[key] = null;
          } else if (value === '' || value === 0) {
            acc[key] = null;
          } else {
            acc[key] = value;
          }
        }
        // Ostatní hodnoty ponechat bez změny
        else {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const response = await api.protocols.patch(protocol.id, sanitizedData);

      if (response.status === 200) {
        setProtocol(response.data);
        setMessage({ type: 'success', message: 'Protokol byl úspěšně uložen.' });
      }
    } catch (error: any) {
      console.error('Chyba při ukládání protokolu:', error);

      let errorMessage = 'Nepodařilo se uložit protokol.';

      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errors = Object.entries(errorData)
            .map(
              ([field, messages]) =>
                `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
            )
            .join('\n');
          errorMessage = `Chyba při ukládání:\n${errors}`;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({ type: 'error', message: errorMessage });
    }
  };

  useEffect(() => {
    if (protocol) {
      methods.reset({
        id: protocol.id,
        protocol_year: protocol.protocol_year,
        protocol_number: protocol.protocol_number,
        deceased: protocol.deceased,
        deceased_display: protocol.deceased_display,
        is_not_autopsied: protocol.is_not_autopsied,
        forensic_consultation: protocol.forensic_consultation,
        autopsy_date: protocol.autopsy_date,
        autopsy_time: protocol.autopsy_time,
        applicant: protocol.applicant,
        applicant_name: protocol.applicant_name,
        blocks: protocol.blocks,
        slides: protocol.slides,
        date_of_death: protocol.date_of_death,
        time_of_death: protocol.time_of_death,
        death_time_unknown: protocol.death_time_unknown,
        death_date_unknown: protocol.death_date_unknown ?? false,
        place_of_death: protocol.place_of_death,
        cause_of_death_autopsy: protocol.cause_of_death_autopsy,
        examining_physician: protocol.examining_physician,
        examining_physician_name: protocol.examining_physician_name,
        examining_physician_text: protocol.examining_physician_text ?? '',
        pathologist1: protocol.pathologist1,
        pathologist1_name: protocol.pathologist1_name,
        pathologist2: protocol.pathologist2,
        pathologist2_name: protocol.pathologist2_name,
        created_by: protocol.created_by,
        created_by_name: protocol.created_by_name,
        lab_technician: protocol.lab_technician,
        lab_technician_name: protocol.lab_technician_name,
        attendees: protocol.attendees,
        classification: protocol.classification,
        classification_name: protocol.classification_name,
        autopsy_diagnosis: protocol.autopsy_diagnosis,
        clinical_dg1: protocol.clinical_dg1,
        clinical_dg2: protocol.clinical_dg2,
        clinical_dg3: protocol.clinical_dg3,
        autopsy_dg1: protocol.autopsy_dg1,
        autopsy_dg2: protocol.autopsy_dg2,
        autopsy_dg3: protocol.autopsy_dg3,
        mechanism_dg1: protocol.mechanism_dg1,
        mechanism_dg2: protocol.mechanism_dg2,
        mechanism_dg3: protocol.mechanism_dg3,
        mechanism_dg4: protocol.mechanism_dg4,
        mechanism_dg5: protocol.mechanism_dg5,
        clinical_dg1_code: protocol.clinical_dg1_code,
        clinical_dg2_code: protocol.clinical_dg2_code,
        clinical_dg3_code: protocol.clinical_dg3_code,
        autopsy_dg1_code: protocol.autopsy_dg1_code,
        autopsy_dg2_code: protocol.autopsy_dg2_code,
        autopsy_dg3_code: protocol.autopsy_dg3_code,
        mechanism_dg1_code: protocol.mechanism_dg1_code,
        mechanism_dg2_code: protocol.mechanism_dg2_code,
        mechanism_dg3_code: protocol.mechanism_dg3_code,
        mechanism_dg4_code: protocol.mechanism_dg4_code,
        mechanism_dg5_code: protocol.mechanism_dg5_code,
        note: protocol.note,
        is_deactivated: protocol.is_deactivated,
        is_locked: protocol.is_locked,
        last_printed_at: protocol.last_printed_at,
        sent_to_uzis_at: protocol.sent_to_uzis_at,
        uzis_identifier: protocol.uzis_identifier,
        is_sent_to_uzis: protocol.is_sent_to_uzis,
        type_of_examination: protocol.examination_type || 0,
        confidentiality_waiver: protocol.confidentiality_waiver || false,
        sent_side_effects_report: protocol.sent_side_effects_report || false,
        sent_to_NOR: protocol.sent_to_NOR || false,
        sent_to_TBC: protocol.sent_to_TBC || false,
        is_interrupted: protocol.is_interrupted || false,
        is_death_time_estimate: protocol.is_death_time_estimate ?? false,
        place_of_death_ruian: protocol.place_of_death_ruian,
        is_stillborn: protocol.is_stillborn ?? false,
        drug_related_death: protocol.drug_related_death ?? false,
        precious_metal_implants: protocol.precious_metal_implants ?? false,
        precious_metal_implants_note: protocol.precious_metal_implants_note ?? '',
      });
    }
  }, [protocol, methods]);

  const togglePopup = () => {
    setMessage(undefined);
  };

  const handleUZIS = async () => {
    if (!protocol?.id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    try {
      const response = await api.protocols.sendToUzis(protocol.id);
      if (response.status === 200) {
        if(response.data.is_sent_to_uzis) {
          setProtocol(response.data);      
        } 
        setMessage({ type: 'success', message: 'Protokol byl odeslán do ÚZIS.' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', message: 'Nepodařilo se odeslat protokol do ÚZIS.' });
    }
  };

  const handleDeactivate = async () => {
    if (!protocol?.id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    try {
      const response = await api.protocols.deactivate(protocol.id);
      if (response.status === 200) {
        setProtocol(response.data);
        setMessage({ type: 'success', message: 'Protokol byl deaktivován.' });
      }
    } catch (error: any) {
      console.error('Chyba při deaktivaci protokolu:', error);
      let errorMessage = 'Nepodařilo se deaktivovat protokol.';
      setMessage({ type: 'error', message: errorMessage });
    }
  };

  const handleActivate = async () => {
    if (!protocol?.id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    try {
      const response = await api.protocols.activate(protocol.id);
      if (response.status === 200) {
        setProtocol(response.data);
        setMessage({ type: 'success', message: 'Protokol byl aktivován.' });
      }
    } catch (error: any) {
      console.error('Chyba při aktivaci protokolu:', error);
      let errorMessage = 'Nepodařilo se aktivovat protokol.';
      setMessage({ type: 'error', message: errorMessage });
    }
  };

  const handleLock = async () => {
    if (!protocol?.id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    try {
      const response = await api.protocols.lock(protocol.id);
      if (response.status === 200) {
        setProtocol(response.data);
        setMessage({ type: 'success', message: 'Protokol byl uzamčen.' });
      }
    } catch (error: any) {
      if(error.status === 403) {
        setMessage({ type: 'info', message: `Nemáte oprávnění k uzamčení protokolu.` });
        return;
      }
      console.error('Chyba při uzamčení protokolu:', error);
      let errorMessage = 'Nepodařilo se uzamknout protokol.';
      setMessage({ type: 'error', message: errorMessage });
    }
  };

  const handleUnLock = async () => {
    if (!protocol?.id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    try {
      const response = await api.protocols.unlock(protocol.id);
      if (response.status === 200) {
        setProtocol(response.data);
        setMessage({ type: 'success', message: 'Protokol byl odemčen.' });
      }
    } catch (error: any) {
      if(error.status === 403) {
        setMessage({ type: 'info', message: `Nemáte oprávnění k odemčení protokolu.` });
        return;
      }
      console.error('Chyba při odemčení protokolu:', error);
      let errorMessage = 'Nepodařilo se odemknout protokol.';
      setMessage({ type: 'error', message: errorMessage });
    }
  };

  const handlePrint = async () => {
    if (!protocol?.id) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    try {
      setPrinting(true);
      const response = await api.protocols.print(protocol.id);
      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
        setMessage({ type: 'success', message: 'Protokol byl odeslán k tisku.' });
      }
    } catch (error: any) {
      console.error('Chyba při tisku protokolu:', error);
      let errorMessage = 'Nepodařilo se vytisknout protokol.';
      setMessage({ type: 'error', message: errorMessage });
    } finally {
      setPrinting(false);
    }
  };

  const watchedExaminationType = methods.watch('type_of_examination');
  const watchedPreciousMetalImplants = methods.watch('precious_metal_implants');
  const examinationTypeCode =
    references.examinationTypes.find((et) => et.id === watchedExaminationType)?.code ?? '';
  const CODE_ZDRAVOTNI = 'zdravotni';

  useEffect(() => {
    if (examinationTypeCode !== CODE_ZDRAVOTNI) {
      methods.clearErrors('applicant');
    }
  }, [examinationTypeCode]);

  const loadRuianOptions = useCallback(async (inputValue: string): Promise<AsyncOption[]> => {
    if (!inputValue || inputValue.length < 2) return [];
    const response = await api.ruian.get({ search: inputValue, limit: 20 });
    return response.data.results.map((a) => ({ value: a.id, label: formatRuianAddress(a) }));
  }, [api.ruian]);

  const debouncedLoadRuianOptions = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (inputValue: string): Promise<AsyncOption[]> =>
      new Promise((resolve) => {
        clearTimeout(timer);
        timer = setTimeout(() => resolve(loadRuianOptions(inputValue)), 300);
      });
  }, [loadRuianOptions]);

  const loadRuianInitialOption = useCallback(async (id: number): Promise<AsyncOption | null> => {
    try {
      const response = await api.ruian.getOneById(id);
      return { value: response.data.id, label: formatRuianAddress(response.data) };
    } catch {
      return null;
    }
  }, [api.ruian]);

  const loadDiagnosesOptions = useCallback(async (inputValue: string): Promise<AsyncOption[]> => {
    const response = await api.references.diagnoses.get({ search: inputValue, limit: 20 });
    return toOptions(response.data.results as Diagnosis[], undefined, (item) => item.code, (item) => item.name || '');
  }, [api.references.diagnoses]);

  const debouncedLoadDiagnosesOptions = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (inputValue: string): Promise<AsyncOption[]> =>
      new Promise((resolve) => {
        clearTimeout(timer);
        timer = setTimeout(() => resolve(loadDiagnosesOptions(inputValue)), 300);
      });
  }, [loadDiagnosesOptions]);

  // Cache for diagnosis options
  const diagnosisCacheRef = useRef<Record<number, AsyncOption>>({});

  const loadDiagnoseOption = useCallback(async (id: number): Promise<AsyncOption | null> => {
    if (!id || Number.isNaN(id)) return null;

    if (diagnosisCacheRef.current[id]) {
      return diagnosisCacheRef.current[id];
    }

    try {
      const response = await api.references.diagnoses.getOneById(id);

      const option: AsyncOption = {
        value: response.data.id,
        label: response.data.code,
        description: response.data.name || ''
      };

      diagnosisCacheRef.current[id] = option;
      return option;
    } catch {
      return null;
    }
  }, [api.references.diagnoses]);

  const fields = {
    autopsy_date: {
      id: 'autopsy_date',
      name: 'autopsy_date',
      label: 'Datum pitvy',
      type: 'date',
      validate: [
        validateDateAfterOrEqual('date_of_death', 'Datum pitvy nesmí být před datem úmrtí'),
        validateMaxDate(new Date(), 'Datum pitvy nemůže být v budoucnosti')
      ]
    },
    autopsy_time: {
      id: 'autopsy_time',
      name: 'autopsy_time',
      label: 'Čas pitvy',
      type: 'time'
    },
    type_of_examination: {
      id: 'type_of_examination',
      name: 'type_of_examination',
      label: 'Typ vyšetření'
    },
    confidentiality_waiver: {
      id: 'confidentiality_waiver',
      name: 'confidentiality_waiver',
      label: 'Prolomení mlčenlivosti (OVM)',
      type: 'checkbox'
    },
    is_interrupted: {
      id: 'is_interrupted',
      name: 'is_interrupted',
      label: 'Přerušení pitvy',
      type: 'checkbox'
    },
    sent_side_effects_report: {
      id: 'sent_side_effects_report',
      name: 'sent_side_effects_report',
      label: 'Hlášení SÚKL nežádoucí účinky',
      type: 'checkbox'
    },
    sent_to_NOR: {
      id: 'sent_to_NOR',
      name: 'sent_to_NOR',
      label: 'Hlášení NOR',
      type: 'checkbox'
    },
    sent_to_TBC: {
      id: 'sent_to_TBC',
      name: 'sent_to_TBC',
      label: 'Hlášení TBC',
      type: 'checkbox'
    },
    payment_type: {
      id: 'payment_type',
      name: 'payment_type',
      label: 'Typ platby'
    },
    applicant: {
      id: 'applicant',
      name: 'applicant',
      label: 'Žadatel',
      required: examinationTypeCode === CODE_ZDRAVOTNI
    },
    slides: {
      id: 'slides',
      name: 'slides'
    },
    blocks: {
      id: 'blocks',
      name: 'blocks'
    },
    date_of_death: {
      id: 'date_of_death',
      name: 'date_of_death',
      label: 'Datum úmrtí',
      type: 'date'
    },
    place_of_death: {
      id: 'place_of_death',
      name: 'place_of_death',
      label: 'Místo úmrtí',
      rows: 2
    },
    time_of_death: {
      id: 'time_of_death',
      name: 'time_of_death',
      label: 'Čas úmrtí',
      type: 'time'
    },
    cause_of_death_autopsy: {
      id: 'cause_of_death_autopsy',
      name: 'cause_of_death_autopsy',
      label: 'Příčina úmrtí dle pitvy',
      rows: 2
    },
    examining_physician: {
      id: 'examining_physician',
      name: 'examining_physician',
      label: 'Prohlížející lékař'
    },
    examining_physician_text: {
      id: 'examining_physician_text',
      name: 'examining_physician_text',
      label: 'Prohlížející lékař (text)',
      required: false,
      maxLength: 255
    },
    pathologist1: {
      id: 'pathologist1',
      name: 'pathologist1',
      label: 'Pitvající 1',
      validate: [
        notEqual(['pathologist2'], 'Pitvající nesmí být stejní')
      ]
    },
    pathologist2: {
      id: 'pathologist2',
      name: 'pathologist2',
      label: 'Pitvající 2',
      validate: [
        notEqual(['pathologist1'], 'Pitvající nesmí být stejní')
      ]
    },
    created_by: {
      id: 'created_by',
      name: 'created_by',
      label: 'Zapsal'
    },
    lab_technician: {
      id: 'lab_technician',
      name: 'lab_technician',
      label: 'Laborant'
    },
    attendees: {
      id: 'attendees',
      name: 'attendees',
      label: 'Přítomni'
    },
    classification: {
      id: 'classification',
      name: 'classification',
      label: 'Klasifikace'
    },
    autopsy_diagnosis: {
      id: 'autopsy_diagnosis',
      name: 'autopsy_diagnosis',
      label: 'Pitevní diagnóza (závěr)',
      rows: 5
    },
    clinical_dg1: {
      id: 'clinical_dg1',
      name: 'clinical_dg1',
      label: 'DG A',
      required: false,
      validate: [
        notEqual(['clinical_dg2', 'clinical_dg3', 'clinical_dg4'], 'Diagnózy nesmí být stejné'),
      ]
    },

    clinical_dg2: {
      id: 'clinical_dg2',
      name: 'clinical_dg2',
      label: 'DG B',
      required: false,
      validate: [
        notEqual(['clinical_dg1', 'clinical_dg3', 'clinical_dg4'], 'Diagnózy nesmí být stejné'),
      ]
    },
    clinical_dg3: {
      id: 'clinical_dg3',
      name: 'clinical_dg3',
      label: 'DG C',
      required: false,
      validate: [
        notEqual(['clinical_dg1', 'clinical_dg2', 'clinical_dg4'], 'Diagnózy nesmí být stejné'),
      ]
    },
    clinical_dg4: {
      id: 'clinical_dg4',
      name: 'clinical_dg4',
      label: 'DG D',
      required: false,
      validate: [
        notEqual(['clinical_dg1', 'clinical_dg2', 'clinical_dg3'], 'Diagnózy nesmí být stejné'),
      ]
    },
    autopsy_dg1: {
      id: 'autopsy_dg1',
      name: 'autopsy_dg1',
      label: 'DG A',
      required: false,
      validate: [
        notEqual(['autopsy_dg2', 'autopsy_dg3'], 'Diagnózy nesmí být stejné'),
      ]
    },
    autopsy_dg2: {
      id: 'autopsy_dg2',
      name: 'autopsy_dg2',
      label: 'DG B',
      required: false,
      validate: [
        notEqual(['autopsy_dg1', 'autopsy_dg3'], 'Diagnózy nesmí být stejné'),
      ]
    },
    autopsy_dg3: {
      id: 'autopsy_dg3',
      name: 'autopsy_dg3',
      label: 'DG C',
      required: false,
      validate: [
        notEqual(['autopsy_dg1', 'autopsy_dg2'], 'Diagnózy nesmí být stejné'),
      ]
    },
    mechanism_dg1: {
      id: 'mechanism_dg1',
      name: 'mechanism_dg1',
      label: 'DG V',
      required: false,
      validate: [
        notEqual(['mechanism_dg2', 'mechanism_dg3', 'mechanism_dg4', 'mechanism_dg5'], 'Diagnózy nesmí být stejné'),
      ]
    },
    mechanism_dg2: {
      id: 'mechanism_dg2',
      name: 'mechanism_dg2',
      label: 'DG W',
      required: false,
      validate: [
        notEqual(['mechanism_dg1', 'mechanism_dg3', 'mechanism_dg4', 'mechanism_dg5'], 'Diagnózy nesmí být stejné'),
      ]
    },
    mechanism_dg3: {
      id: 'mechanism_dg3',
      name: 'mechanism_dg3',
      label: 'DG X',
      required: false,
      validate: [
        notEqual(['mechanism_dg1', 'mechanism_dg2', 'mechanism_dg4', 'mechanism_dg5'], 'Diagnózy nesmí být stejné'),
      ]
    },
    mechanism_dg4: {
      id: 'mechanism_dg4',
      name: 'mechanism_dg4',
      label: 'DG Y',
      required: false,
      validate: [
        notEqual(['mechanism_dg1', 'mechanism_dg2', 'mechanism_dg3', 'mechanism_dg5'], 'Diagnózy nesmí být stejné'),
      ]
    },
    mechanism_dg5: {
      id: 'mechanism_dg5',
      name: 'mechanism_dg5',
      label: 'DG Z',
      required: false,
      validate: [
        notEqual(['mechanism_dg1', 'mechanism_dg2', 'mechanism_dg3', 'mechanism_dg4'], 'Diagnózy nesmí být stejné'),
      ]
    },
    note: {
      id: 'note',
      name: 'note',
      label: 'Poznámka',
      required: false
    },
    is_death_time_estimate: {
      id: 'is_death_time_estimate',
      name: 'is_death_time_estimate',
      label: 'Čas úmrtí určen odhadem'
    },
    place_of_death_ruian: {
      id: 'place_of_death_ruian',
      name: 'place_of_death_ruian',
      label: 'Adresa úmrtí'
    },
    is_stillborn: {
      id: 'is_stillborn',
      name: 'is_stillborn',
      label: 'Mrtvě narozené dítě'
    },
    drug_related_death: {
      id: 'drug_related_death',
      name: 'drug_related_death',
      label: 'Úmrtí v souvislosti s užitím návykové látky'
    },
    precious_metal_implants: {
      id: 'precious_metal_implants',
      name: 'precious_metal_implants',
      label: 'Nalezeny nesnímatelné náhrady nebo protézy z drahých kovů při pitvě'
    },
    precious_metal_implants_note: {
      id: 'precious_metal_implants_note',
      name: 'precious_metal_implants_note',
      label: 'Popis nesnímatelné náhrady nebo protézy z drahých kovů',
      rows: 3,
      required: watchedPreciousMetalImplants === true
    }
  };

  return {
    methods,
    fields,
    message,
    loading: loading || referencesLoading,
    loadRuianOptions: debouncedLoadRuianOptions,
    loadRuianInitialOption,
    loadDiagnosesOptions: debouncedLoadDiagnosesOptions,
    loadDiagnoseOption,
    printing,
    options: {
      examinationType: toOptions(references.examinationTypes),
      persons: toOptions(references.persons),
      employees: toOptions(references.employees),
      examiningPhysicians: toOptions(references.examiningPhysicians, undefined, (item) => item.name + (item.organization_name ? ` - ${item.organization_name}` : '')),
      applicants: toOptions(references.applicants),
      paymentTypes: toOptions(references.paymentTypes),
      deathClassifications: toOptions(references.deathClassifications),
      diagnoses: toOptions(references.diagnoses, undefined, (item) => item.code, (item) => item.name || '')
    },
    onSubmit,
    togglePopup,
    handleDeactivate,
    handleActivate,
    handleLock,
    handleUnLock,
    handlePrint,
    handleUZIS
  };
};
